// The two buttons every report ends with, and the one thing that can go wrong.
//
// Shared so the User Access dialog and the Installed Devices page cannot drift
// into offering different exports of the same model — and so the native-download
// failure is reported once, in one sentence, rather than twice in two wordings.

import React from 'react';
import { Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import TableViewIcon from '@mui/icons-material/TableView';
import { printReport } from './printReport';
import { downloadBytes, XLSX_MIME, NATIVE_DOWNLOAD_UNSUPPORTED } from './downloadFile';
import { reportToSheets, reportFileName } from '../../core/admin/accessReport';
import { buildWorkbook } from '../../core/utils/xlsx';
import { isNativePlatform } from '../../core/platform';

/**
 * `onError(message)` is called when the workbook cannot be saved.
 *
 * ⚠️ A DOWNLOAD THAT CANNOT START IS INVISIBLE — no file appears and nothing
 * throws — so the one environment where that happens is said out loud. See
 * ui/shared/downloadFile for why the native app is that environment.
 */
export default function ReportActions({ report, onError }) {
  const saveExcel = () => {
    if (!report) return;
    try {
      downloadBytes(reportFileName(report), buildWorkbook(reportToSheets(report)), XLSX_MIME);
    } catch (e) {
      if (!onError) return;
      onError(e && e.code === NATIVE_DOWNLOAD_UNSUPPORTED
        ? 'Excel export needs a browser — open this page in Chrome or Edge instead of the app.'
        : 'That workbook could not be saved.');
    }
  };

  // ⚠️ PRINTING FAILS IN THE APP THE SAME WAY DOWNLOADING DOES — silently.
  //
  // An Android WebView does not implement `window.print()`: Capacitor never
  // wires Android's PrintManager to it, so the call is a no-op. printReport
  // builds its hidden iframe, injects the sheet, calls print() on the frame and
  // returns, having done nothing visible. The operator presses "Print / Save as
  // PDF" and the screen does not change — which reads as the report being
  // broken, not as printing being unavailable.
  //
  // This mattered the moment /installed and /user-access were routed into the
  // MOBILE shell, which is native by definition. Before that the two reports
  // existed only on desktop, where the shell all but implies a real browser.
  // (All but: `localStorage.uiMode = 'desktop'` overrides the native check, so
  // the desktop shell could already reach here from inside the app — the same
  // hole downloadFile documents.)
  //
  // Same treatment as the workbook: refuse out loud rather than appear to work.
  const doPrint = () => {
    if (!report) return;
    if (isNativePlatform()) {
      if (onError) {
        onError('Printing needs a browser — open this page in Chrome or Edge instead of the app.');
      }
      return;
    }
    printReport(report);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<TableViewIcon />}
        disabled={!report}
        onClick={saveExcel}
      >
        Excel
      </Button>
      <Button
        variant="contained"
        startIcon={<PrintIcon />}
        disabled={!report}
        onClick={doPrint}
      >
        Print / Save as PDF
      </Button>
    </>
  );
}
